// Country calling codes for the phone input country selector.
// Emoji flags are used for a lightweight, dependency-free flag display.

export const DEFAULT_COUNTRY = 'PL'

export const COUNTRIES = [
  { code: 'PL', name: 'Polska', dialCode: '+48', flag: '🇵🇱' },
  { code: 'DE', name: 'Niemcy', dialCode: '+49', flag: '🇩🇪' },
  { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
  { code: 'BE', name: 'Belgia', dialCode: '+32', flag: '🇧🇪' },
  { code: 'BG', name: 'Bułgaria', dialCode: '+359', flag: '🇧🇬' },
  { code: 'HR', name: 'Chorwacja', dialCode: '+385', flag: '🇭🇷' },
  { code: 'CY', name: 'Cypr', dialCode: '+357', flag: '🇨🇾' },
  { code: 'CZ', name: 'Czechy', dialCode: '+420', flag: '🇨🇿' },
  { code: 'DK', name: 'Dania', dialCode: '+45', flag: '🇩🇰' },
  { code: 'EE', name: 'Estonia', dialCode: '+372', flag: '🇪🇪' },
  { code: 'FI', name: 'Finlandia', dialCode: '+358', flag: '🇫🇮' },
  { code: 'FR', name: 'Francja', dialCode: '+33', flag: '🇫🇷' },
  { code: 'GR', name: 'Grecja', dialCode: '+30', flag: '🇬🇷' },
  { code: 'ES', name: 'Hiszpania', dialCode: '+34', flag: '🇪🇸' },
  { code: 'NL', name: 'Holandia', dialCode: '+31', flag: '🇳🇱' },
  { code: 'IE', name: 'Irlandia', dialCode: '+353', flag: '🇮🇪' },
  { code: 'IS', name: 'Islandia', dialCode: '+354', flag: '🇮🇸' },
  { code: 'CA', name: 'Kanada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'LI', name: 'Liechtenstein', dialCode: '+423', flag: '🇱🇮' },
  { code: 'LT', name: 'Litwa', dialCode: '+370', flag: '🇱🇹' },
  { code: 'LU', name: 'Luksemburg', dialCode: '+352', flag: '🇱🇺' },
  { code: 'LV', name: 'Łotwa', dialCode: '+371', flag: '🇱🇻' },
  { code: 'MT', name: 'Malta', dialCode: '+356', flag: '🇲🇹' },
  { code: 'MD', name: 'Mołdawia', dialCode: '+373', flag: '🇲🇩' },
  { code: 'MC', name: 'Monako', dialCode: '+377', flag: '🇲🇨' },
  { code: 'NO', name: 'Norwegia', dialCode: '+47', flag: '🇳🇴' },
  { code: 'PT', name: 'Portugalia', dialCode: '+351', flag: '🇵🇹' },
  { code: 'RO', name: 'Rumunia', dialCode: '+40', flag: '🇷🇴' },
  { code: 'SK', name: 'Słowacja', dialCode: '+421', flag: '🇸🇰' },
  { code: 'SI', name: 'Słowenia', dialCode: '+386', flag: '🇸🇮' },
  { code: 'CH', name: 'Szwajcaria', dialCode: '+41', flag: '🇨🇭' },
  { code: 'SE', name: 'Szwecja', dialCode: '+46', flag: '🇸🇪' },
  { code: 'HU', name: 'Węgry', dialCode: '+36', flag: '🇭🇺' },
  { code: 'GB', name: 'Wielka Brytania', dialCode: '+44', flag: '🇬🇧' },
  { code: 'IT', name: 'Włochy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'US', name: 'Stany Zjednoczone', dialCode: '+1', flag: '🇺🇸' },
]

export const COUNTRY_BY_CODE = Object.fromEntries(
  COUNTRIES.map((c) => [c.code, c])
)

/**
 * Detect the country code for a given E.164 phone number by matching the
 * longest matching dial code. Falls back to the provided default.
 */
export function detectCountryFromPhone(phone, defaultCode = DEFAULT_COUNTRY) {
  if (!phone || typeof phone !== 'string' || !phone.startsWith('+')) {
    return defaultCode
  }

  // Sort by longest dial code first so +44 matches before +4 (if +4 existed).
  const sorted = [...COUNTRIES].sort(
    (a, b) => b.dialCode.length - a.dialCode.length
  )
  for (const country of sorted) {
    if (phone.startsWith(country.dialCode)) {
      return country.code
    }
  }
  return defaultCode
}

/**
 * Strip the country dial code from an E.164 number, returning the national
 * subscriber number. Returns the full input if no match is found.
 */
export function stripDialCode(phone, countryCode) {
  if (!phone || typeof phone !== 'string') return ''
  const country = COUNTRY_BY_CODE[countryCode]
  if (!country) return phone.replace(/^\+/, '')
  if (phone.startsWith(country.dialCode)) {
    return phone.slice(country.dialCode.length)
  }
  return phone.replace(/^\+/, '')
}

/**
 * Build an E.164 string from a country code and a national number.
 */
export function buildE164(countryCode, nationalNumber) {
  const digits = (nationalNumber || '').replace(/\D/g, '')
  if (!digits) return ''
  const country = COUNTRY_BY_CODE[countryCode]
  const dialCode = country ? country.dialCode : ''
  return `${dialCode}${digits}`
}

/**
 * Validate an E.164 phone number: + followed by 7 to 15 digits.
 */
export function isValidPhone(phone) {
  if (!phone || typeof phone !== 'string') return false
  return /^\+[1-9]\d{6,14}$/.test(phone)
}
